import type {
  ReactNode,
  ComponentProps,
  ComponentPropsWithoutRef,
  ComponentRef,
  HTMLAttributes
} from 'react';
import { createContext, useContext, forwardRef, useId } from 'react';
import type * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';

import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  FormProvider,
  useFormContext
} from 'react-hook-form';

import { cn } from '@r/utils/utils.ts';
import { Label } from './label.tsx';
import { cva, type VariantProps } from 'class-variance-authority';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/tooltip.tsx';
import { BadgeInfo } from 'lucide-react';
import type { ZodObject, ZodRawShape, ZodTypeAny } from 'zod';
import {
  getNestedSchema,
  extractDescriptionFromZodType,
  isFieldRequired
} from '@r/utils/zod-utils.ts';

//==============================================================================// FORM

// Context value for providing the Zod schema to all form fields
// This allows fields to access the global form schema for validation, tooltips, etc.
type FormSchemaContextValue = {
  schema?: ZodObject<ZodRawShape>;
};

const FormSchemaContext = createContext<FormSchemaContextValue>({});

const useFormSchema = () => {
  return useContext(FormSchemaContext);
};

interface FormProps {
  schema?: ZodObject<ZodRawShape>;
  children: ReactNode;
}

// Form component that provides the schema context and react-hook-form context
const Form = <TFieldValues extends FieldValues = FieldValues>({
  schema,
  children,
  ...formProviderProps
}: FormProps & ComponentProps<typeof FormProvider<TFieldValues>>) => {
  return (
    <FormSchemaContext.Provider value={{ schema }}>
      <FormProvider {...formProviderProps}>{children}</FormProvider>
    </FormSchemaContext.Provider>
  );
};

Form.displayName = 'Form';

//==============================================================================// FORM FIELD

// Context value for a single form field, including its name and optional field-specific schema
// fieldSchema allows a field to override the global form schema for its own validation/description
// This is useful for dynamic fields or fields with custom rules
//
type FormFieldContextValue = {
  name: string;
  fieldSchema?: ZodTypeAny;
};

const FormFieldContext = createContext<FormFieldContextValue>({} as FormFieldContextValue);

// FormField component wraps react-hook-form's Controller and provides field context
// Accepts an optional fieldSchema prop to override the global schema for this field
const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  fieldSchema,
  ...props
}: { fieldSchema?: ZodTypeAny } & ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name, fieldSchema }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = useContext(FormFieldContext);
  const itemContext = useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    fieldSchema: fieldContext.fieldSchema, // Expose fieldSchema for per-field overrides
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState
  };
};

//==============================================================================// FORM ITEM

type FormItemContextValue = {
  id: string;
};

const FormItemContext = createContext<FormItemContextValue>({} as FormItemContextValue);

const FormItem = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const id = useId();

    return (
      <FormItemContext.Provider value={{ id }}>
        <div ref={ref} className={cn('space-y-2', className)} {...props} />
      </FormItemContext.Provider>
    );
  }
);

FormItem.displayName = 'FormItem';

//==============================================================================// FORM LABEL

const FormLabel = forwardRef<
  ComponentRef<typeof LabelPrimitive.Root>,
  ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { formItemId, fieldSchema, name } = useFormField();
  const { schema } = useFormSchema();

  const currentFieldSchema = fieldSchema ?? getNestedSchema(schema, name);
  const fieldMessages = extractDescriptionFromZodType(currentFieldSchema);

  return (
    <div className="flex gap-2 items-center">
      <div className="flex items-start gap-0.5">
        <Label ref={ref} className={cn(className)} htmlFor={formItemId} {...props} />

        {schema && isFieldRequired(currentFieldSchema) && (
          <span className="text-sm text-destructive">*</span>
        )}
      </div>

      {schema && fieldMessages && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <BadgeInfo size={16} className="mb-1" />
            </TooltipTrigger>
            <TooltipContent className="max-w-[20vw]">
              <p>{fieldMessages}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
});

FormLabel.displayName = 'FormLabel';

//==============================================================================// FORM CONTROL

const FormControl = forwardRef<ComponentRef<typeof Slot>, ComponentPropsWithoutRef<typeof Slot>>(
  ({ ...props }, ref) => {
    const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

    return (
      <Slot
        ref={ref}
        id={formItemId}
        aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
        aria-invalid={!!error}
        {...props}
      />
    );
  }
);

FormControl.displayName = 'FormControl';

//==============================================================================// FORM DESCRIPTION

const formDescriptionVariants = cva(
  'mt-1 font-light peer-disabled:cursor-not-allowed peer-disabled:opacity-30',
  {
    variants: {
      variant: {
        default: 'text-foreground',
        error: 'text-red-500'
      },
      descriptionSize: {
        default: 'text-sm pl-[0.125rem]',
        sm: 'text-xs pl-[0.063]',
        md: 'text-sm pl-[0.125rem]',
        lg: 'text-lg pl-[0.188rem]'
      }
    },
    defaultVariants: {
      variant: 'default',
      descriptionSize: 'default'
    }
  }
);

interface FormDescriptionProps
  extends HTMLAttributes<HTMLParagraphElement>,
    VariantProps<typeof formDescriptionVariants> {
  className?: string;
  error?: boolean;
  variant?: 'default' | 'error';
  descriptionSize?: 'default' | 'sm' | 'md' | 'lg';
}

const FormDescription = forwardRef<HTMLParagraphElement, FormDescriptionProps>(
  ({ error, className, variant, descriptionSize, ...props }, ref) => {
    const { formDescriptionId } = useFormField();

    if (error) {
      variant = 'error';
    }

    return (
      <p
        ref={ref}
        id={formDescriptionId}
        className={cn(formDescriptionVariants({ variant, descriptionSize, className }))}
        {...props}
      />
    );
  }
);

FormDescription.displayName = 'FormDescription';

//==============================================================================// FORM MESSAGE

const FormMessage = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, children, ...props }, ref) => {
    const { error, formMessageId } = useFormField();
    const body = error ? String(error?.message) : children;

    if (!body) {
      return null;
    }

    return (
      <p
        ref={ref}
        id={formMessageId}
        className={cn('text-sm font-medium text-destructive', className)}
        {...props}
      >
        {body}
      </p>
    );
  }
);

FormMessage.displayName = 'FormMessage';

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField
};
